import { Injectable } from '@angular/core';
export interface BuildInstallationMarkersArgs {
  installations: any[];
  map: google.maps.Map;
  statusLastTransmission: Record<string, any>;
  formatDatetime: (datetimeString: any) => number | null; // retorna a diferença de datas em horas pra facilitar
  alertingInstallationReferences: Map<string, string>;
  onClick: (marker: google.maps.Marker) => void;
  clusterToleranceDegrees?: number;
  hideAfterHours?: number;
}

export interface BuildInstallationMarkersResult {
  markers: google.maps.Marker[];
  installationMacAddress: any[];
  markersByClusterKey: Map<string, google.maps.Marker[]>;
  totalVisibleInstallations: number;
}

@Injectable({ providedIn: 'root' })
export class AuditMapMarkerService {
  buildInstallationMarkers(
    args: BuildInstallationMarkersArgs,
  ): BuildInstallationMarkersResult {
    const {
      installations,
      map,
      statusLastTransmission,
      formatDatetime,
      alertingInstallationReferences,
      onClick,
      clusterToleranceDegrees = 0.00003,
      hideAfterHours = 3,
    } = args;

    const markers: google.maps.Marker[] = [];
    const installationMacAddress: any[] = [];
    const markersByClusterKey = new Map<string, google.maps.Marker[]>();

    const visibleInstallations = (installations ?? []).filter(
      (installation: any) => {
        const mac = installation?.properties?.device_mac;
        const reference = installation?.properties?.reference as
          | string
          | undefined;
        const transmission = statusLastTransmission?.[mac];
        const diffHours = formatDatetime(transmission?.last_transmission);
        const isAlerting =
          !!reference && alertingInstallationReferences.has(reference);
        // Instalações em alerta permanecem visíveis mesmo com transmissão antiga.
        if (isAlerting) {
          return true;
        }
        // Demais: ocultar se última transmissão ausente ou acima do limite (ex.: 3 h).
        return diffHours != null && diffHours <= hideAfterHours;
      },
    );

    visibleInstallations.forEach((installation: any) => {
      const lat = installation.geometry.coordinates[1];
      const lng = installation.geometry.coordinates[0];

      installationMacAddress.push(installation?.properties?.device_mac);

      const marker: google.maps.Marker = new google.maps.Marker({
        position: { lat, lng },
        title: installation.properties.reference,
        clickable: true,
        map,
      });

      const deviceMac = installation.properties.device_mac?.toUpperCase();
      const equipmentInfo = statusLastTransmission?.[deviceMac];

      marker.set('id', installation.properties.id);
      marker.set('division_id', installation.properties.division_id);
      marker.set('gateway_id', installation.properties.gateway_id);
      marker.set('equipment_type', installation.properties.equipment_type);
      marker.set('status', equipmentInfo?.status == '1' ? true : false);
      marker.set(
        'has_magnetic_key',
        installation.properties.possible_magnectic_key,
      );
      marker.set(
        'device_mac',
        installation.properties.device_mac != 'NULL'
          ? installation.properties.device_mac
          : null,
      );
      marker.set('last_transmission', equipmentInfo?.last_transmission ?? null);
      marker.set('applications', installation.properties.applicationtag);
      marker.set(
        'relay',
        equipmentInfo?.relay === '1'
          ? true
          : equipmentInfo?.relay === '0'
            ? false
            : null,
      );

      const key = this.clusterKey(lat, lng, clusterToleranceDegrees);
      marker.set('cluster_key', key);
      const group = markersByClusterKey.get(key) ?? [];
      group.push(marker);
      markersByClusterKey.set(key, group);

      this.applyGroupLabel(group);

      const iconUrl = this.resolveInstallationIconUrl({
        marker,
        alertingInstallationReferences,
      });
      marker.setIcon({
        url: iconUrl,
        size: new google.maps.Size(38, 38),
        scaledSize: new google.maps.Size(38, 38),
      });

      marker.addListener('click', () => onClick(marker));
      markers.push(marker);
    });

    return {
      markers,
      installationMacAddress,
      markersByClusterKey,
      totalVisibleInstallations: visibleInstallations.length,
    };
  }

  clusterKey(lat: number, lng: number, toleranceDegrees: number): string {
    const tol = toleranceDegrees || 0.00003;
    const latKey = Math.round(lat / tol);
    const lngKey = Math.round(lng / tol);
    return `${latKey}:${lngKey}`;
  }

  private applyGroupLabel(group: google.maps.Marker[]) {
    const count = group.length;
    group.forEach((marker) => {
      if (count > 1) {
        marker.setLabel({
          text: count.toString(),
          fontSize: '15px',
          fontWeight: 'bold',
          color: 'black',
          className: 'bg-light p-1 pt-0 pb-0 rounded-circle',
        });
      } else {
        marker.setLabel(null);
      }
    });
  }

  private resolveInstallationIconUrl(args: {
    marker: google.maps.Marker;
    alertingInstallationReferences: Map<string, string>;
  }): string {
    const { marker, alertingInstallationReferences } = args;

    const reference = marker.getTitle() ?? '';
    if (reference && alertingInstallationReferences.has(reference)) {
      return '../../../../assets/mark-icons/installation-red.png';
    }

    const status = marker.get('status');
    if (status === true) {
      return '../../../../assets/mark-icons/installation-yellow.png';
    }
    return '../../../../assets/mark-icons/installation-gray.png';
  }
}
